import { IVendorDistribution } from '../types';

/**
 * Adjusts distribution values to maintain a total of 100%
 */
export const adjustDistributionValues = (
    values: IVendorDistribution[],
    index: number,
    newValue: number
): {
    updatedValues: IVendorDistribution[];
    errorMessage: string | null;
    isValid: boolean;
} => {
    const updatedValues = [...values];
    const oldValue = updatedValues[index].odds;
    const difference = newValue - oldValue;

    // Update the changed value
    updatedValues[index].odds = newValue;
    let errorMessage: string | null = null;

    if (difference !== 0) {
        // Get values that can be adjusted (exclude the current one)
        const adjustableValues = updatedValues
            .map((item, i) => ({ value: item.odds, index: i }))
            .filter(item => item.index !== index);

        if (adjustableValues.length > 0) {
            if (difference > 0) {
                // Need to decrease other values
                errorMessage = distributeDecrease(updatedValues, index, difference, adjustableValues);
            } else {
                // Need to increase other values
                distributeIncrease(updatedValues, difference, adjustableValues);
            }
        } else {
            // If there are no other values, this value must be 100%
            updatedValues[index].odds = 100;
        }
    }

    // Ensure no negative values
    updatedValues.forEach(item => {
        if (item.odds < 0) item.odds = 0;
    });

    // Final check to ensure total is 100
    const finalTotal = calculateTotal(updatedValues);
    const isValid = finalTotal === 100;

    if (finalTotal === 100) {
        errorMessage = null;
    }

    return {
        updatedValues,
        errorMessage,
        isValid
    };
};

/**
 * Calculate total of all distribution values
 */
export const calculateTotal = (values: IVendorDistribution[]): number => {
    return values.reduce((sum, item) => sum + item.odds, 0);
};

/**
 * Distribute a decrease across other values
 */
const distributeDecrease = (
    updatedValues: IVendorDistribution[],
    changedIndex: number,
    difference: number,
    adjustableValues: { value: number; index: number }[]
): string | null => {
    // Sort by highest value first to take from them
    adjustableValues.sort((a, b) => b.value - a.value);

    let remainingAdjustment = difference;
    let errorMessage: string | null = null;

    // Try to distribute the adjustment across all other values
    for (let i = 0; i < adjustableValues.length && remainingAdjustment > 0; i++) {
        const itemIndex = adjustableValues[i].index;
        const currentValue = updatedValues[itemIndex].odds;
        const maxDecrease = Math.min(currentValue, remainingAdjustment);

        updatedValues[itemIndex].odds = currentValue - maxDecrease;
        remainingAdjustment -= maxDecrease;
    }

    // If we couldn't adjust enough, reduce the current value
    if (remainingAdjustment > 0) {
        updatedValues[changedIndex].odds = updatedValues[changedIndex].odds - remainingAdjustment;
        errorMessage = "Cannot maintain 100% total. Other values are already at minimum.";
    }

    return errorMessage;
};

/**
 * Distribute an increase across other values
 */
const distributeIncrease = (
    updatedValues: IVendorDistribution[],
    difference: number,
    adjustableValues: { value: number; index: number }[]
): void => {
    // Sort by lowest value first to give to them
    adjustableValues.sort((a, b) => a.value - b.value);

    let remainingAdjustment = -difference;

    // Distribute the increase proportionally
    for (let i = 0; i < adjustableValues.length && remainingAdjustment > 0; i++) {
        const itemIndex = adjustableValues[i].index;
        updatedValues[itemIndex].odds += Math.ceil(remainingAdjustment / (adjustableValues.length - i));
        remainingAdjustment = 100 - calculateTotal(updatedValues);
    }
};